import { G } from '@mobily/ts-belt';

import { B2bCashSource } from '@Common/types/bo/b2b/b2b';
import { GetB2bCampaignOneResponse } from '@Common/types/bo/b2b/campaigns';

import { b2bCampaignTypeChoices } from '@sh4res/bo-common/components/B2bCampaigns/choices';

/**
 * A matching contribution campaign is run on top of another cash source. For voluntary deposits, the
 * matching contribution either targets the deposits made by the employees themselves or a company
 * voluntary deposit campaign, which the single `voluntary_deposit` cash source cannot express. The
 * form therefore offers two choices, both saved as the `voluntary_deposit` cash source:
 * - employee voluntary deposits: no related campaign (`related_campaign_id` stays null),
 * - company voluntary deposits: the abounded campaign is mandatory (`related_campaign_id` is set).
 *
 * The presence of a related campaign is what tells both apart, on the form and once persisted.
 */
export const EMPLOYEE_VOLUNTARY_DEPOSIT_CASH_SOURCE = 'employee_voluntary_deposit';
export const COMPANY_VOLUNTARY_DEPOSIT_CASH_SOURCE = 'company_voluntary_deposit';

const EMPLOYEE_VOLUNTARY_DEPOSIT_LABEL =
  'resources.bo_common.b2b.campaigns.choices.type.employee_voluntary_deposit';
const COMPANY_VOLUNTARY_DEPOSIT_LABEL =
  'resources.bo_common.b2b.campaigns.choices.type.voluntary_deposit';

/**
 * Value of the form-only `matching_contribution_related_cash_source_input` field: a cash source,
 * with `voluntary_deposit` replaced by its employee / company variants.
 */
export type MatchingContributionRelatedCashSourceInput =
  | Exclude<B2bCashSource, 'voluntary_deposit'>
  | typeof EMPLOYEE_VOLUNTARY_DEPOSIT_CASH_SOURCE
  | typeof COMPANY_VOLUNTARY_DEPOSIT_CASH_SOURCE;

export const isCompanyVoluntaryDepositCashSourceInput = (
  input: MatchingContributionRelatedCashSourceInput | null | undefined,
): boolean => input === COMPANY_VOLUNTARY_DEPOSIT_CASH_SOURCE;

export const isEmployeeVoluntaryDepositCashSourceInput = (
  input: MatchingContributionRelatedCashSourceInput | null | undefined,
): boolean => input === EMPLOYEE_VOLUNTARY_DEPOSIT_CASH_SOURCE;

/**
 * Cash source to persist for the given form choice: both voluntary deposit choices collapse back to
 * the `voluntary_deposit` cash source.
 */
export const toMatchingContributionRelatedCashSource = (
  input: MatchingContributionRelatedCashSourceInput | null | undefined | '',
): B2bCashSource | null => {
  if (G.isNullable(input) || input === '') {
    return null;
  }
  return isEmployeeVoluntaryDepositCashSourceInput(input) ||
    isCompanyVoluntaryDepositCashSourceInput(input)
    ? 'voluntary_deposit'
    : (input as B2bCashSource);
};

/**
 * Form choice matching an already saved campaign, so editing one preselects the right option.
 */
export const toMatchingContributionRelatedCashSourceInput = (
  campaign?: Pick<
    GetB2bCampaignOneResponse,
    'matching_contribution_related_cash_source' | 'related_campaign_id'
  > | null,
): MatchingContributionRelatedCashSourceInput | null => {
  const cashSource = campaign?.matching_contribution_related_cash_source;
  if (G.isNullable(cashSource)) {
    return null;
  }
  if (cashSource !== 'voluntary_deposit') {
    return cashSource;
  }
  return G.isNotNullable(campaign?.related_campaign_id)
    ? COMPANY_VOLUNTARY_DEPOSIT_CASH_SOURCE
    : EMPLOYEE_VOLUNTARY_DEPOSIT_CASH_SOURCE;
};

/**
 * Campaign type choices where the `voluntary_deposit` entry is replaced by the employee and company
 * variants, used by the matching contribution cash source select.
 */
export const splitVoluntaryDepositCashSourceChoices = (choices: { id: string; name: string }[]) =>
  choices.flatMap((choice) =>
    choice.id === 'voluntary_deposit'
      ? [
          {
            ...choice,
            id: EMPLOYEE_VOLUNTARY_DEPOSIT_CASH_SOURCE,
            name: EMPLOYEE_VOLUNTARY_DEPOSIT_LABEL,
          },
          {
            ...choice,
            id: COMPANY_VOLUNTARY_DEPOSIT_CASH_SOURCE,
            name: COMPANY_VOLUNTARY_DEPOSIT_LABEL,
          },
        ]
      : [choice],
  );

/**
 * Display choices for a saved cash source: `voluntary_deposit` reads as employee or company
 * voluntary deposits depending on whether the campaign abounds another campaign.
 */
export const getMatchingContributionRelatedCashSourceDisplayChoices = (
  campaign?: Pick<GetB2bCampaignOneResponse, 'related_campaign_id'> | null,
) =>
  b2bCampaignTypeChoices.map((choice) =>
    choice.id === 'voluntary_deposit' && G.isNullable(campaign?.related_campaign_id)
      ? { ...choice, name: EMPLOYEE_VOLUNTARY_DEPOSIT_LABEL }
      : choice,
  );
